import React, { useState } from 'react';

interface CourtApplicationProps {
    caseData: any;
    onUpdate: () => void;
}

export default function CourtApplication({ caseData, onUpdate }: CourtApplicationProps) {
    const [selectedPath, setSelectedPath] = useState<'accelerated' | 'standard' | null>(null);

    // Checklist state
    const [checklist, setChecklist] = useState({
        tenancyAgreement: { label: 'Written Tenancy Agreement', ready: false, docs: ['Original signed AST'] },
        depositProtection: { label: 'Deposit Protection Certificate', ready: false, docs: ['Prescribed Information', 'Deposit Certificate'] },
        noticeServed: { label: 'Valid Notice Served', ready: false, docs: ['Section 8 Notice', 'Proof of Postage/Service'] },
        n215: { label: 'Certificate of Service (Form N215)', ready: false, docs: ['N215 completed correctly'] },
        epc: { label: 'Energy Performance Certificate', ready: false, docs: ['Valid EPC provided to tenant at start'] },
        gasSafety: { label: 'Gas Safety Record', ready: false, docs: ['Last 2 annual certificates provided'] },
        howToRent: { label: '"How to Rent" Guide', ready: false, docs: ['Current version provided to tenant at start'] },
        courtFee: { label: 'Court Fee (£355)', ready: false, docs: ['Payment reference or cheque'] },
    });

    const toggleItem = (item: keyof typeof checklist) => {
        setChecklist(prev => ({
            ...prev,
            [item]: { ...prev[item], ready: !prev[item].ready }
        }));
    };

    const isReady = Object.values(checklist).every(v => v.ready);

    return (
        <div className="space-y-6 animate-fadeIn">
            <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-r-lg shadow-sm">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <i className="fas fa-gavel text-indigo-400"></i>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-bold text-indigo-800 uppercase tracking-wide">Court Application Guidance</h3>
                        <div className="mt-2 text-sm text-indigo-700">
                            <p>You have marked the notice period as expired. The next legal step is a court application for a possession order.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Path Selection */}
            {!selectedPath ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div
                        onClick={() => setSelectedPath('accelerated')}
                        className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-8 cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all group text-center shadow-sm"
                    >
                        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-indigo-600 transition-colors">
                            <i className="fas fa-bolt text-indigo-600 text-2xl group-hover:text-white"></i>
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">Accelerated Procedure (N5B)</h4>
                        <p className="text-sm text-gray-500 mt-2">Fastest route. No court hearing usually required. Use if only seeking possession (not rent arrears).</p>
                    </div>

                    <div
                        onClick={() => setSelectedPath('standard')}
                        className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-8 cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all group text-center shadow-sm"
                    >
                        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-indigo-600 transition-colors">
                            <i className="fas fa-balance-scale text-indigo-600 text-2xl group-hover:text-white"></i>
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">Standard Procedure (N5)</h4>
                        <p className="text-sm text-gray-500 mt-2">Requires a hearing. Use if claiming rent arrears or for discretionary grounds (Nuisance, Breach).</p>
                    </div>
                </div>
            ) : (
                <div className="space-y-8">
                    {/* Active Path Header */}
                    <div className="flex items-center justify-between bg-white p-5 rounded-xl shadow-md border border-gray-100">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg transform rotate-3">
                                <i className={`fas ${selectedPath === 'accelerated' ? 'fa-bolt' : 'fa-balance-scale'} text-white text-xl`}></i>
                            </div>
                            <div>
                                <h4 className="font-extrabold text-gray-900 text-lg">
                                    {selectedPath === 'accelerated' ? 'Accelerated Possession (Form N5B)' : 'Standard Possession (Form N5)'}
                                </h4>
                                <span className="text-xs text-indigo-500 font-bold uppercase tracking-wider flex items-center">
                                    <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2 animate-pulse"></span>
                                    Phase: Documentation & Submission
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={() => setSelectedPath(null)}
                            className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-semibold transition-all"
                        >
                            Change Procedure
                        </button>
                    </div>

                    {/* Checklist & Steps */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Checklist Section */}
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                            <div className="bg-gray-900 px-6 py-5 flex items-center justify-between">
                                <div>
                                    <h5 className="font-bold text-white text-lg">Evidence Pack Checklist</h5>
                                    <p className="text-xs text-gray-400">Strict requirements for court acceptance</p>
                                </div>
                                <div className="text-xs font-bold text-gray-400">
                                    {Object.values(checklist).filter(v => v.ready).length}/{Object.values(checklist).length} COMPLETE
                                </div>
                            </div>
                            <div className="p-4 divide-y divide-gray-50">
                                {Object.entries(checklist).map(([key, val]) => (
                                    <div
                                        key={key}
                                        onClick={() => toggleItem(key as keyof typeof checklist)}
                                        className={`group flex items-start p-4 rounded-xl cursor-pointer transition-all ${val.ready ? 'bg-green-50/50' : 'hover:bg-gray-50/80'}`}
                                    >
                                        <div className={`mt-1 w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${val.ready ? 'bg-green-500 border-green-500 scale-110 shadow-green-200 shadow-md' : 'bg-white border-gray-200 shadow-sm'}`}>
                                            {val.ready && <i className="fas fa-check text-white text-[12px]"></i>}
                                        </div>
                                        <div className="ml-4 flex-1">
                                            <div className="flex justify-between items-center">
                                                <span className={`text-sm font-bold ${val.ready ? 'text-green-800' : 'text-gray-700'}`}>
                                                    {val.label}
                                                </span>
                                                {val.ready ? (
                                                    <span className="text-[9px] font-black bg-green-200 text-green-800 px-2 py-0.5 rounded-full uppercase tracking-tighter">Verified</span>
                                                ) : (
                                                    <span className="text-[9px] font-black bg-red-100 text-red-600 px-2 py-0.5 rounded-full uppercase tracking-tighter">Required</span>
                                                )}
                                            </div>
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                {val.docs.map((doc, idx) => (
                                                    <span key={idx} className="text-[10px] bg-white border border-gray-100 text-gray-500 px-1.5 py-0.5 rounded-md shadow-sm italic">
                                                        {doc}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {!isReady && (
                                <div className="p-4 bg-red-600 text-white text-xs font-bold flex items-center justify-center space-x-3">
                                    <i className="fas fa-exclamation-circle animate-bounce"></i>
                                    <span>FATAL: One or more critical documents are missing for court submission.</span>
                                </div>
                            )}
                        </div>

                        {/* Submission Guidance */}
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                                <div className="bg-gray-50 px-6 py-5 border-b border-gray-100">
                                    <h5 className="font-bold text-gray-900 text-lg">Automated Document Preparation</h5>
                                </div>
                                <div className="p-6 space-y-6">
                                    <div className="flex space-x-4">
                                        <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-bold shadow-md">1</div>
                                        <div className="flex-1">
                                            <h6 className="font-extrabold text-gray-900 text-sm">Download Court Forms (N5B/N5)</h6>
                                            <p className="text-xs text-gray-500 mt-1">Interactive PDF forms provided by GOV.UK.</p>
                                            <a
                                                href={selectedPath === 'accelerated' ? "https://www.gov.uk/government/publications/form-n5b-england-claim-form-for-possession-of-a-property-located-wholly-in-england-accelerated-procedure" : "https://www.gov.uk/government/publications/form-n5-claim-form-for-possession-of-property"}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="group mt-3 inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-black hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                                            >
                                                <i className="fas fa-file-pdf mr-2 group-hover:scale-110 transition-transform"></i>
                                                GET {selectedPath === 'accelerated' ? 'N5B' : 'N5'} TEMPLATE
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex space-x-4 border-t border-gray-50 pt-6">
                                        <div className="flex-shrink-0 w-10 h-10 bg-red-600 text-white rounded-xl flex items-center justify-center font-bold shadow-md">2</div>
                                        <div className="flex-1">
                                            <div className="flex justify-between">
                                                <h6 className="font-extrabold text-gray-900 text-sm">Download Evidence Pack (PDF Bundle)</h6>
                                                <span className="text-[10px] bg-green-600 text-white px-2 py-0.5 rounded-full uppercase font-black tracking-widest">Ready</span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">Automated bundle of your uploaded agreements, certificates, and proof of service.</p>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const { evictionService } = await import('../../services/api');
                                                        const res = await evictionService.getCaseBundle(caseData.id);
                                                        if (res.data?.success && res.data?.pdfBase64) {
                                                            const link = document.createElement('a');
                                                            link.href = `data:application/pdf;base64,${res.data.pdfBase64}`;
                                                            link.download = `Evidence_Pack_${caseData.id}.pdf`;
                                                            link.click();
                                                        }
                                                    } catch (e) {
                                                        console.error("Bundle failed", e);
                                                        alert("Bundle generation failed. Please ensure all documents are valid.");
                                                    }
                                                }}
                                                className="mt-3 inline-flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-lg text-xs font-black hover:bg-red-600 hover:text-white transition-all shadow-sm border border-red-100"
                                            >
                                                <i className="fas fa-file-pdf mr-2"></i> DOWNLOAD EVIDENCE PACK
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex space-x-4 border-t border-gray-50 pt-6">
                                        <div className="flex-shrink-0 w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center font-bold shadow-md">3</div>
                                        <div className="flex-1">
                                            <h6 className="font-extrabold text-gray-900 text-sm">Possession Claim Online (PCOL)</h6>
                                            <p className="text-xs text-gray-500 mt-1">Complete your application and pay the £355 fee electronically.</p>
                                            <a
                                                href="https://www.gov.uk/possession-claim-online-recover-property"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="group mt-3 inline-flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-black hover:bg-emerald-600 hover:text-white transition-all shadow-sm border border-emerald-100"
                                            >
                                                <i className="fas fa-external-link-alt mr-2 group-hover:scale-110 transition-transform"></i>
                                                ACCESS PCOL SYSTEM
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Enforcement Guidance */}
                            <div className="bg-gray-900 rounded-2xl shadow-xl overflow-hidden text-white">
                                <div className="bg-white/10 px-6 py-5 flex items-center">
                                    <i className="fas fa-shield-alt mr-3 text-xl text-emerald-400"></i>
                                    <h5 className="font-black text-lg uppercase tracking-tight">Post-Court Enforcement</h5>
                                </div>
                                <div className="p-6">
                                    <p className="text-sm font-medium leading-relaxed opacity-90">
                                        If the tenant disregards the court's Possession Order, you must apply for a <strong>Warrant of Possession (Form N325)</strong> to authorize County Court Bailiffs.
                                    </p>
                                    <div className="mt-6 flex flex-col space-y-3">
                                        <a
                                            href="https://www.gov.uk/government/publications/form-n325-request-for-warrant-for-possession-of-land"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="group flex items-center justify-between bg-white text-emerald-800 p-3 rounded-xl hover:bg-emerald-50 transition-all font-black text-xs shadow-lg"
                                        >
                                            <span className="flex items-center">
                                                <i className="fas fa-file-invoice mr-3 bg-emerald-100 p-2 rounded-lg group-hover:bg-emerald-600 group-hover:text-white transition-all"></i>
                                                GET FORM N325 (WARRANT)
                                            </span>
                                            <i className="fas fa-download opacity-30"></i>
                                        </a>
                                        <button
                                            onClick={() => alert("Bailiff guidance pack will be sent to your admin email.")}
                                            className="text-xs flex items-center justify-center p-3 border-2 border-white/20 rounded-xl hover:bg-white/10 transition-all font-bold tracking-wider text-emerald-100 hover:text-white"
                                        >
                                            <i className="fas fa-info-circle mr-2"></i>
                                            REQUEST BAILIFF GUIDANCE PACK
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
